import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Badge } from 'react-bootstrap';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Header from '~/Components/Header/Header';
import TabelaDinamica from '~/Components/TabelaDinamica/Tabela';
import Loading from '~/Components/Loading';
import ModalConfirmarExclusao from './components/ModalConfirmarExclusao';
import {
  listarGesParaVinculo,
  listarContasPorGe,
  listarVinculos,
  incluirVinculo,
} from '~/Services/Service';
import { customStyles, DropdownIndicator } from '~/Styles/reactSelectStyles';

const COLUNAS_VINCULOS = [
  { title: 'GE', key: 'ge_vinculo', ordenacao: true, width: '60px' },
  { title: 'Conta', key: 'conta_vinculo', ordenacao: true, width: '70px' },
  { title: 'Apelido', key: 'apelido_conta', ordenacao: true },
  { title: 'CNPJ', key: 'cnpj', ordenacao: false },
  { title: 'Agência', key: 'agencia', ordenacao: false, width: '90px' },
  { title: 'Nº Conta', key: 'numero_conta', ordenacao: false, width: '100px' },
  { title: 'Convênio PG', key: 'pg_convenio', ordenacao: false, width: '110px' },
];

const RelacionamentoContas = () => {
  const [ges, setGes] = useState([]);
  const [geSelected, setGeSelected] = useState(null);

  const [contas, setContas] = useState([]);
  const [contaSelected, setContaSelected] = useState(null);
  const [loadingContas, setLoadingContas] = useState(false);

  const [vinculos, setVinculos] = useState([]);
  const [loadingVinculos, setLoadingVinculos] = useState(false);

  // Painel de inclusão
  const [geVinculoSelected, setGeVinculoSelected] = useState(null);
  const [contasVinculo, setContasVinculo] = useState([]);
  const [contaVinculoSelected, setContaVinculoSelected] = useState(null);
  const [loadingContasVinculo, setLoadingContasVinculo] = useState(false);
  const [incluindo, setIncluindo] = useState(false);

  // Modal exclusão
  const [vinculoParaExcluir, setVinculoParaExcluir] = useState(null);
  const [showModalExcluir, setShowModalExcluir] = useState(false);

  // Carrega GEs no mount
  useEffect(() => {
    listarGesParaVinculo()
      .then(res => {
        const dados = (res.data && res.data.dados) ? res.data.dados : [];
        setGes(
          dados.map(ge => ({
            value: ge.codigo_ge,
            label: `${ge.codigo_ge} - ${ge.apelido}`,
          }))
        );
      })
      .catch(err => {
        const msg = err && err.message ? err.message : 'Erro ao carregar GEs';
        toast.error(msg);
      });
  }, []);

  // Carregar contas ao mudar GE principal — flag anti-race (LICOES Ciclo 9 L3)
  useEffect(() => {
    if (!geSelected) {
      setContas([]);
      setContaSelected(null);
      setVinculos([]);
      setGeVinculoSelected(null);
      setContasVinculo([]);
      setContaVinculoSelected(null);
      return;
    }

    let ativo = true;
    setLoadingContas(true);
    setContaSelected(null);
    setVinculos([]);
    setGeVinculoSelected(null);
    setContasVinculo([]);
    setContaVinculoSelected(null);

    listarContasPorGe(geSelected.value)
      .then(res => {
        if (!ativo) return;
        setContas((res.data && res.data.dados) ? res.data.dados : []);
      })
      .catch(err => {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar contas';
        toast.error(msg);
      })
      .finally(() => { if (ativo) setLoadingContas(false); });

    return () => { ativo = false; };
  }, [geSelected]);

  // Carregar vínculos ao mudar conta principal — flag anti-race (LICOES Ciclo 9 L3)
  useEffect(() => {
    if (!contaSelected) {
      setVinculos([]);
      return;
    }

    let ativo = true;
    setLoadingVinculos(true);
    setVinculos([]);

    listarVinculos(contaSelected.codigo_ge, contaSelected.codigo_conta)
      .then(res => {
        if (!ativo) return;
        setVinculos((res.data && res.data.dados) ? res.data.dados : []);
      })
      .catch(err => {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar vínculos';
        toast.error(msg);
      })
      .finally(() => { if (ativo) setLoadingVinculos(false); });

    return () => { ativo = false; };
  }, [contaSelected]);

  // Carregar contas vinculáveis ao mudar GE vínculo — flag anti-race (LICOES Ciclo 9 L3)
  useEffect(() => {
    if (!geVinculoSelected || !contaSelected) {
      setContasVinculo([]);
      setContaVinculoSelected(null);
      return;
    }

    let ativo = true;
    setLoadingContasVinculo(true);
    setContaVinculoSelected(null);
    setContasVinculo([]);

    listarContasPorGe(
      geVinculoSelected.value,
      contaSelected.banco,
      geSelected ? geSelected.value : null
    )
      .then(res => {
        if (!ativo) return;
        setContasVinculo((res.data && res.data.dados) ? res.data.dados : []);
      })
      .catch(err => {
        if (!ativo) return;
        const msg = err && err.message ? err.message : 'Erro ao carregar contas vinculáveis';
        toast.error(msg);
      })
      .finally(() => { if (ativo) setLoadingContasVinculo(false); });

    return () => { ativo = false; };
  }, [geVinculoSelected, contaSelected, geSelected]);

  const recarregarVinculos = () => {
    if (!contaSelected) return;
    setLoadingVinculos(true);
    listarVinculos(contaSelected.codigo_ge, contaSelected.codigo_conta)
      .then(res => setVinculos((res.data && res.data.dados) ? res.data.dados : []))
      .catch(err => {
        const msg = err && err.message ? err.message : 'Erro ao recarregar vínculos';
        toast.error(msg);
      })
      .finally(() => setLoadingVinculos(false));
  };

  const handleIncluir = () => {
    if (!geVinculoSelected || !contaVinculoSelected || !contaSelected || !geSelected) return;

    setIncluindo(true);
    incluirVinculo({
      codigo_ge_principal: geSelected.value,
      codigo_conta_principal: contaSelected.codigo_conta,
      codigo_ge_vinculo: geVinculoSelected.value,
      codigo_conta_vinculo: contaVinculoSelected.codigo_conta,
    })
      .then(() => {
        toast.success('Vínculo incluído com sucesso!');
        setGeVinculoSelected(null);
        setContasVinculo([]);
        setContaVinculoSelected(null);
        recarregarVinculos();
      })
      .catch(err => {
        const msg = err && err.message ? err.message : 'Erro ao incluir vínculo';
        toast.error(msg);
      })
      .finally(() => setIncluindo(false));
  };

  const handleExcluirClick = vinculo => {
    setVinculoParaExcluir(vinculo);
    setShowModalExcluir(true);
  };

  const handleExcluirConfirmar = () => {
    setShowModalExcluir(false);
    setVinculoParaExcluir(null);
    recarregarVinculos();
  };

  const handleFecharModal = () => {
    setShowModalExcluir(false);
    setVinculoParaExcluir(null);
  };

  const contasOptions = contas.map(c => ({
    value: c.codigo_conta,
    label: `${c.codigo_conta} - ${c.apelido_conta}`,
    data: c,
  }));

  const gesVinculoOptions = ges.filter(
    ge => !geSelected || ge.value !== geSelected.value
  );

  const contasVinculoOptions = contasVinculo.map(c => ({
    value: c.codigo_conta,
    label: `${c.codigo_conta} - ${c.apelido_conta}`,
    data: c,
  }));

  const contaSelectedOption = contaSelected
    ? contasOptions.find(o => o.value === contaSelected.codigo_conta)
    : null;

  const contaVinculoOption = contaVinculoSelected
    ? contasVinculoOptions.find(o => o.value === contaVinculoSelected.codigo_conta)
    : null;

  return (
    <>
      <Header title="Relacionamento de Contas" />
      <div className="container-fluid mt-3">

        {/* Card — Conta Principal */}
        <Card className="mb-3">
          <Card.Header><strong>Conta Principal</strong></Card.Header>
          <Card.Body>
            <Row>
              <Col xs={12} md={4} className="mb-3 mb-md-0">
                <label className="small mb-1">Grupo Empresa</label>
                <Select
                  options={ges}
                  value={geSelected}
                  onChange={opt => setGeSelected(opt)}
                  styles={customStyles}
                  components={{ DropdownIndicator }}
                  placeholder="Selecione a GE..."
                  isClearable
                  noOptionsMessage={() => 'Nenhuma GE disponível'}
                />
              </Col>
              <Col xs={12} md={8}>
                <label className="small mb-1">Conta Bancária</label>
                {loadingContas ? (
                  <Loading />
                ) : (
                  <Select
                    options={contasOptions}
                    value={contaSelectedOption}
                    onChange={opt => setContaSelected(opt ? opt.data : null)}
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    placeholder={geSelected ? 'Selecione a conta...' : 'Selecione a GE primeiro'}
                    isDisabled={!geSelected}
                    isClearable
                    noOptionsMessage={() => 'Nenhuma conta elegível para esta GE'}
                  />
                )}
              </Col>
            </Row>

            {contaSelected && (
              <Row className="mt-3">
                <Col>
                  <Card className="bg-light border-0">
                    <Card.Body className="py-2">
                      <Row>
                        <Col xs={6} sm={4} md={2} className="mb-2">
                          <small className="text-muted d-block">CNPJ</small>
                          <strong>{contaSelected.cnpj || '—'}</strong>
                        </Col>
                        <Col xs={6} sm={8} md={4} className="mb-2">
                          <small className="text-muted d-block">Razão Social</small>
                          <strong>{contaSelected.razao_social || '—'}</strong>
                        </Col>
                        <Col xs={6} sm={4} md={2} className="mb-2">
                          <small className="text-muted d-block">Agência</small>
                          <strong>
                            {contaSelected.agencia}
                            {contaSelected.dig_agencia ? `-${contaSelected.dig_agencia}` : ''}
                          </strong>
                        </Col>
                        <Col xs={6} sm={4} md={2} className="mb-2">
                          <small className="text-muted d-block">Nº Conta</small>
                          <strong>
                            {contaSelected.numero_conta}
                            {contaSelected.dig_conta ? `-${contaSelected.dig_conta}` : ''}
                          </strong>
                        </Col>
                        <Col xs={6} sm={4} md={2} className="mb-2">
                          <small className="text-muted d-block">Convênio PG</small>
                          <strong>{contaSelected.pg_convenio || '—'}</strong>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}
          </Card.Body>
        </Card>

        {/* Card — Vínculos */}
        {contaSelected && (
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Vínculos</strong>
              {vinculos.length > 0 && (
                <Badge variant="secondary">{vinculos.length} vínculo(s)</Badge>
              )}
            </Card.Header>
            <Card.Body>
              {loadingVinculos ? (
                <div className="text-center py-3"><Loading /></div>
              ) : vinculos.length === 0 ? (
                <p className="text-muted text-center py-3 mb-0">
                  Nenhum vínculo cadastrado para esta conta.
                </p>
              ) : (
                <TabelaDinamica
                  colunas={COLUNAS_VINCULOS}
                  dados={vinculos}
                  acoes={[
                    {
                      label: 'Excluir',
                      type: 'danger',
                      onClick: handleExcluirClick,
                    },
                  ]}
                  striped
                  hover
                />
              )}
            </Card.Body>
          </Card>
        )}

        {/* Card — Incluir Vínculo */}
        {contaSelected && (
          <Card className="mb-3">
            <Card.Header><strong>Incluir Vínculo</strong></Card.Header>
            <Card.Body>
              <Row className="align-items-end">
                <Col xs={12} md={4} className="mb-3 mb-md-0">
                  <label className="small mb-1">GE Vinculada</label>
                  <Select
                    options={gesVinculoOptions}
                    value={geVinculoSelected}
                    onChange={opt => {
                      setGeVinculoSelected(opt);
                      setContaVinculoSelected(null);
                    }}
                    styles={customStyles}
                    components={{ DropdownIndicator }}
                    placeholder="Selecione a GE..."
                    isClearable
                    noOptionsMessage={() => 'Nenhuma GE disponível'}
                  />
                </Col>
                <Col xs={12} md={6} className="mb-3 mb-md-0">
                  <label className="small mb-1">Conta Vinculada (mesmo banco)</label>
                  {loadingContasVinculo ? (
                    <Loading />
                  ) : (
                    <Select
                      options={contasVinculoOptions}
                      value={contaVinculoOption}
                      onChange={opt => setContaVinculoSelected(opt ? opt.data : null)}
                      styles={customStyles}
                      components={{ DropdownIndicator }}
                      placeholder={
                        geVinculoSelected
                          ? 'Selecione a conta...'
                          : 'Selecione a GE vinculada primeiro'
                      }
                      isDisabled={!geVinculoSelected}
                      isClearable
                      noOptionsMessage={() =>
                        'Nenhuma conta elegível (mesmo banco) nesta GE'
                      }
                    />
                  )}
                </Col>
                <Col xs={12} md={2}>
                  <Button
                    variant="primary"
                    onClick={handleIncluir}
                    disabled={!geVinculoSelected || !contaVinculoSelected || incluindo}
                    className="w-100"
                  >
                    {incluindo ? 'Incluindo...' : 'Incluir'}
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </div>

      {showModalExcluir && vinculoParaExcluir && (
        <ModalConfirmarExclusao
          show={showModalExcluir}
          vinculo={vinculoParaExcluir}
          gePrincipal={geSelected ? geSelected.value : null}
          contaPrincipal={contaSelected ? contaSelected.codigo_conta : null}
          onConfirmar={handleExcluirConfirmar}
          onFechar={handleFecharModal}
        />
      )}
    </>
  );
};

export default RelacionamentoContas;
